/* global RequestInit */
export const NAMESPACE_ERROR_MESSAGE = 'Namespace not found for API Key, make sure you set it up.';
export function decodeApiKey(encodedApiKey) {
    try {
        return atob(encodedApiKey);
    }
    catch {
        throw new Error('Failed to decode API Key: invalid base64 string.');
    }
}
export async function fetchAppState({ encodedApiKey, authenticationUrl }) {
    if (!encodedApiKey || typeof encodedApiKey !== 'string') {
        throw new Error('encodedApiKey is required and must be a base64 encoded string');
    }
    const apiKey = decodeApiKey(encodedApiKey);
    const response = await fetch(`${authenticationUrl}/app-state`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'x-api-key': apiKey,
        },
    });
    if (response.status === 401) {
        throw new Error(NAMESPACE_ERROR_MESSAGE);
    }
    return handleApiResponseWithHeaders(response);
}
export async function postAppState({ state, encodedApiKey, authenticationUrl }) {
    if (typeof state !== 'object' && typeof state !== 'string') {
        throw new Error('State must be an object or a string.');
    }
    if (!encodedApiKey || typeof encodedApiKey !== 'string') {
        throw new Error('encodedApiKey is required and must be a base64 encoded string');
    }
    const apiKey = decodeApiKey(encodedApiKey);
    const body = typeof state === 'string' ? state : JSON.stringify(state);
    const response = await fetch(`${authenticationUrl}/app-state`, {
        credentials: 'include',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
        },
        body,
    });
    if (response.status === 401) {
        throw new Error(NAMESPACE_ERROR_MESSAGE);
    }
    return response.json();
}
export function fetchUser({ authenticationUrl }) {
    return fetch(authenticationUrl, {
        credentials: 'include',
        method: 'GET',
    }).then(response => response.json());
}
function extractSessionHeaders(response) {
    return {
        visitorCountry: response.headers.get('x-viewer-country'),
        visitorIP: response.headers.get('x-viewer-ip'),
        visitorIsBot: parseBooleanHeader(response.headers.get('x-viewer-is-bot')),
        visitorIsInternal: parseBooleanHeader(response.headers.get('x-viewer-is-internal')),
    };
}
export function parseBooleanHeader(value) {
    if (value === 'true') {
        return true;
    }
    if (value === 'false') {
        return false;
    }
    return null;
}
export function handleApiResponseWithHeaders(response) {
    return response.json().then(body => ({
        body,
        headers: extractSessionHeaders(response),
    }));
}
export function fetchSession({ authenticationUrl, viewerUri }) {
    return fetch(`${authenticationUrl}/session`, {
        credentials: 'include',
        method: 'GET',
        ...(viewerUri && { headers: { 'x-viewer-uri': viewerUri } }),
    }).then(handleApiResponseWithHeaders);
}
export function fetchUserProfileInfo({ authenticationUrl, jwt }) {
    const options = generateFetchOptions({ method: 'GET', jwt });
    return fetch(`${authenticationUrl}/profile`, options).then(response => response.json());
}
// Remove this once Notifications are fully removed
export function fetchNotifications({ authenticationUrl, jwt }) {
    const options = generateFetchOptions({ method: 'GET', jwt });
    return fetch(`${authenticationUrl}/profile/notifications?sortDir=desc&size=5`, options).then(response => response.json());
}
// Remove this once Notifications are fully removed
export function fetchNotificationSummary({ authenticationUrl, jwt }) {
    const options = generateFetchOptions({ method: 'GET', jwt });
    return fetch(`${authenticationUrl}/profile/notifications/summary`, options).then(response => response.json());
}
// Remove this once Notifications are fully removed
export function dismissNotifications({ authenticationUrl, jwt }) {
    const now = new Date(Date.now()).toISOString();
    const body = { startDate: now };
    const options = generateFetchOptions({ method: 'PUT', jwt, body: JSON.stringify(body) });
    return fetch(`${authenticationUrl}/profile/notifications/dismiss`, options).then(response => response.json());
}
export function generateFetchOptions({ method, jwt }) {
    const headers = new Headers();
    if (jwt) {
        headers.set('authorization', `Bearer ${jwt}`);
    }
    const options = {
        method,
        credentials: 'include',
        headers,
    };
    return options;
}
